import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Layers, Loader2, Users } from 'lucide-react';
import { getPublicCollection, getPublicCollections } from '../services/cms';

const CollectionDetail = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    setData(null); setErr(false);
    getPublicCollection(slug).then(setData).catch(() => setErr(true));
    getPublicCollections().then((all) => setOthers(all.filter((c) => c.slug !== slug))).catch(() => setOthers([]));
  }, [slug]);

  if (err) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="collection-unavailable">
      <p className="font-display text-xl mb-4">Cette collection est indisponible.</p>
      <Link to="/collections" className="text-primary font-800">Retour aux collections</Link>
    </div>
  );
  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const { collection, comics } = data;
  const hero = comics.find((c) => c.featured_image?.url)?.featured_image?.url;

  return (
    <div data-testid="collection-detail-page">
      <div className="relative h-64 sm:h-80 overflow-hidden bg-[#141414]">
        {hero && <img src={hero} alt={collection.name} className="w-full h-full object-cover opacity-60" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 pb-8 w-full">
            <Link to="/collections" className="inline-flex items-center gap-2 text-white/90 font-700 mb-4 hover:text-secondary transition-colors"><ArrowLeft className="w-4 h-4" /> Collections</Link>
            <span className="flex items-center gap-2 text-secondary font-800 text-sm mb-1"><Layers className="w-4 h-4" /> {comics.length} webtoon{comics.length > 1 ? 's' : ''}</span>
            <h1 className="font-display font-700 text-3xl sm:text-5xl text-white">{collection.name}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {comics.length === 0 ? (
          <p className="text-muted-foreground font-600">Les webtoons de cette collection arrivent bientôt.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
            {comics.map((e, i) => (
              <Link key={e.source_id} to={`/fiche/${encodeURIComponent(e.slug)}?collection=${encodeURIComponent(collection.slug)}`}
                data-testid={`collection-comic-${e.slug}`}
                className="card-hover group bg-card rounded-2xl overflow-hidden border border-border animate-float-up" style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}>
                <div className="relative aspect-square overflow-hidden">
                  {e.featured_image?.url ? <img src={e.featured_image.url} alt={e.titre} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : <div className="w-full h-full bg-muted" />}
                  <span className="absolute top-2 left-2 w-7 h-7 rounded-full bg-primary text-white text-xs font-800 flex items-center justify-center shadow-lg" data-testid={`collection-order-${e.slug}`}>{e.position}</span>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-3">
                  <h3 className="font-display font-600 text-sm leading-snug line-clamp-2 min-h-[36px] group-hover:text-primary transition-colors">{e.titre}</h3>
                  {(e.auteurs || []).length > 0 && (
                    <p className="text-[11px] text-muted-foreground font-700 mt-1 flex items-center gap-1 truncate"><Users className="w-3 h-3 shrink-0" /> {e.auteurs.map((a) => a.name).join(', ')}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {others.length > 0 && (
          <div className="mt-16">
            <h3 className="font-display font-700 text-2xl mb-6">Autres collections</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {others.slice(0, 4).map((c) => (
                <Link key={c.slug} to={`/collection/${encodeURIComponent(c.slug)}`} className="card-hover group relative rounded-2xl overflow-hidden h-36">
                  {c.cover ? <img src={c.cover} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : <div className="w-full h-full bg-muted" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <h4 className="absolute bottom-3 left-3 right-3 font-display font-600 text-white text-sm leading-tight">{c.name}</h4>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionDetail;
